import { Injectable } from '@angular/core';
import { LocalDataSource } from 'ng2-smart-table';
import { LocalStorageUtilWrapperService } from './local-storage-util-wrapper.service';
import { Observable } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { convertObjectToMap } from '../utils/object-utils';

export class StatefulFiltersInitiator {
  tableName: string;
  tableConfig: any;
  tableSource: LocalDataSource;
  updateQueryParams?: boolean = false;
  sessionPersistence?: boolean = false;
}

@Injectable({
  providedIn: 'root',
})
export class TableStatefulFiltersService {
  private LOCAL_STORAGE_KEY = 'tableFiltersStates';

  // This map will store the state of all the filters which need to be applied
  private tableStates: Map<string, Map<string, any>> = new Map<
    string,
    Map<string, any>
  >();

  // Stores the observables for all the table sources against the tableNames
  private tableObservers: Map<string, Observable<any>> = new Map<
    string,
    Observable<any>
  >();

  // Stores all the source of the tables by reference so that the filters
  // can be applied directly from the service itself using a generic function
  private tableSources: Map<string, LocalDataSource> = new Map<
    string,
    LocalDataSource
  >();

  // Set to store all the tables for which changes need to reflected in the URL as query params
  private URLtoBeProcessedFor: Set<string> = new Set<string>();

  constructor(
    private localStorageWrapperService: LocalStorageUtilWrapperService,
    private router: Router,
    private _route: ActivatedRoute,
  ) {}

  // Adds an entry to the tableStates map when a component is initialized
  initializeRecording(args: StatefulFiltersInitiator) {
    // Required to avoid ambiguity
    if (args.updateQueryParams) {
      this.URLtoBeProcessedFor.add(args.tableName);
    }
    this.getFilterStateFromLocalStorage(args.tableName);

    if (!this.tableStates?.has(args.tableName)) {
      let filterStateMap = new Map<string, any>();
      Object.keys(args.tableConfig?.columns).forEach((colConfig) => {
        filterStateMap.set(colConfig, null);
      });
      this.tableStates.set(args.tableName, filterStateMap);
    }

    if (!args.sessionPersistence) {
      this.tableStates.get(args.tableName)?.clear();
    }

    this.processQueryParams(args.tableName);
    this.tableSources.set(args.tableName, args.tableSource);
    this.tableObservers.set(args.tableName, args.tableSource.onChanged());
    this.recordFilterState(args.tableName);
    this.setForFirstLoad(args.tableName, args.tableConfig);
  }

  // Checks if the table has just loaded and if there are any filters present and applies them if needed
  setForFirstLoad(tableName: string, tableConfig: any) {
    const subscription = this.tableObservers
      .get(tableName)
      .subscribe((changes) => {
        this.tableObservers.delete(tableName);
        if (changes.action === 'load') {
          if (!this.tableObservers.get(tableName)) {
            subscription.unsubscribe();
          }
          this.applyFilters(tableName, tableConfig);
        }
      });
  }

  // Whenever there are any changes inside any tables which is using this service,
  // we update the state here and in the local storage
  recordFilterState(tableName: string) {
    this.tableObservers.get(tableName).subscribe((change) => {
      if (change?.action === 'filter') {
        const currentTableStates =
          this.getFilterStateFromLocalStorage(tableName);
        currentTableStates.clear();
        change?.filter?.filters?.forEach((filter) => {
          if (filter?.search?.length) {
            currentTableStates.set(filter?.field, filter?.search);
          } else {
            currentTableStates.delete(filter?.field);
          }
        });
        this.tableStates.set(tableName, currentTableStates);
        this.localStorageWrapperService.set(
          this.LOCAL_STORAGE_KEY,
          this.serializeMap(this.tableStates),
        );
        this.addQueryParamsToRoute(tableName);
      }
    });
  }

  // Fetches previously recorded states of all the filters from local storage
  fetchPreviousStatesFromLocalStorage(): Map<string, Map<string, any>> {
    const storedData = this.localStorageWrapperService.get(
      this.LOCAL_STORAGE_KEY,
    );
    if (!storedData) {
      return new Map<string, Map<string, any>>();
    }

    let filtersMap = new Map<string, Map<string, any>>();
    Object.keys(storedData).forEach((tableName) => {
      filtersMap.set(tableName, convertObjectToMap(storedData[tableName]));
    });
    return filtersMap;
  }

  // Returns previously stored state for a particular tableName
  getFilterStateFromLocalStorage(tableName: string) {
    if (
      !this.tableStates.size ||
      !(this.tableStates.get(tableName) instanceof Map)
    ) {
      this.tableStates = this.fetchPreviousStatesFromLocalStorage();
    }
    return this.tableStates.get(tableName);
  }

  // Take the source of the table and apply the filters to it
  applyFilters(tableName: string, tableConfig: any) {
    const source = this.tableSources.get(tableName);
    const filtersMap = this.getFilterStateFromLocalStorage(tableName);
    function getFiltersArray(map: Map<string, any>) {
      let resultArray: { field: string; search: string }[] = [];
      Array.from(map?.keys()).forEach((colName: string) => {
        if (map.get(colName)?.length) {
          resultArray.push({
            field: colName,
            search: map.get(colName),
          });
        }
      });
      return resultArray;
    }

    // Filters array should only have names from columns
    let filtersArray = getFiltersArray(filtersMap);

    // Create a set of all the available columns from the tableConfig
    // and only allow the query params which are actually associated to
    // the table config, this will get rid of all the invalid query params
    const columnSet: Set<string> = new Set<string>();
    Object.keys(tableConfig?.columns)?.forEach((colName) =>
      columnSet.add(colName),
    );
    filtersArray = filtersArray.filter((filter) => columnSet.has(filter.field));

    // Apply the filters on the source
    source.setFilter(filtersArray);
    this.addQueryParamsToRoute(tableName);
  }

  // Adds query params to the URL based on the applied filters
  addQueryParamsToRoute(tableName: string) {
    if (!this.URLtoBeProcessedFor.has(tableName)) {
      return;
    }
    const filtersMap = this.getFilterStateFromLocalStorage(tableName);
    const queryParams: Params = Object.fromEntries(filtersMap.entries());
    this.router.navigate([], {
      relativeTo: this._route,
      queryParams,
    });
  }

  // Process query params if present and create a map of the same and add it to tableStates
  // Query params would take precedence and if they are present we'll flush out the data we might
  // have stored before this
  processQueryParams(tableName: string) {
    if (!this.URLtoBeProcessedFor.has(tableName)) {
      return;
    }
    const queryParams = this._route.snapshot.queryParamMap;
    if (!queryParams?.keys?.length) {
      return;
    }
    this.tableStates.get(tableName)?.clear();
    Array.from(queryParams?.keys).forEach((columnName) => {
      if (!this.tableStates.has(tableName)) {
        this.tableStates.set(tableName, new Map<string, any>());
      }
      this.tableStates
        .get(tableName)
        .set(columnName, queryParams.get(columnName));
    });
  }

  // Adds a prefix to the name of the table to avoid clashes with other keys from other components
  getLocalTableNameKey(tableName: string) {
    return `filterState_${tableName}`;
  }

  // Serialize the Map of Map
  serializeMap(map: Map<string, Map<string, any>>): {
    [key: string]: { [innerKey: string]: any };
  } {
    const serializedValue: { [key: string]: { [innerKey: string]: any } } = {};
    map.forEach((innerMap: any, innerKey) => {
      serializedValue[innerKey] = {};
      if (innerMap instanceof Map) {
        innerMap.forEach((innerValue, innerValueKey) => {
          serializedValue[innerKey][innerValueKey] = innerValue;
        });
      } else {
        for (const innerValueKey in innerMap) {
          if (innerMap.hasOwnProperty(innerValueKey)) {
            serializedValue[innerKey][innerValueKey] = innerMap[innerValueKey];
          }
        }
      }
    });
    return serializedValue;
  }

  // Flush all the data out for the stateful filters from local storage
  flushFilterStates() {
    this.localStorageWrapperService.remove(this.LOCAL_STORAGE_KEY);
  }
}
